import React, { useEffect, useState, useRef } from "react";
import { useIntegrationsStore } from "@/hooks/useIntegrations";
import { API_URL } from "@/env";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import Jira from "@/assets/icons/Jira.svg";
import Slack from "@/assets/icons/Slack.svg";
import Linear from "@/assets/icons/Linear.svg";
import Asana from "@/assets/icons/Asana.svg";
import ClickUp from "@/assets/icons/ClickUp.svg";
import Gitlab from "@/assets/icons/Gitlab.svg";
import Notion from "@/assets/icons/Notion.svg";
import Sentry from "@/assets/icons/Sentry.svg";
import Azure from "@/assets/icons/Azure.svg";
import Github from "@/assets/icons/Github.svg";
import VerticalMenu from "@/assets/icons/VerticalMenu.svg";
import CheckWhite from "@/assets/icons/CheckWhite.svg";
import Logout from "@/assets/icons/Logout.svg";
import { motion, AnimatePresence } from "motion/react";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { getCookie } from "@/lib/cookies";
import { showCustomToast } from "../CustomToast";

const integrationsList = [
  {
    name: "Slack",
    icon: Slack,
  },
  {
    name: "Asana",
    icon: Asana,
  },
  {
    name: "ClickUp",
    icon: ClickUp,
  },
  {
    name: "GitLab",
    icon: Gitlab,
  },
  {
    name: "Notion",
    icon: Notion,
  },
  {
    name: "Azure DevOps",
    icon: Azure,
  },
  {
    name: "GitHub",
    icon: Github,
  },
];

const SettingsIntegrations = () => {
  const [isOpenJiraDropdown, setIsOpenJiraDropdown] = useState(false);
  const [isOpenLinearDropdown, setIsOpenLinearDropdown] = useState(false);
  const [isOpenSentryDropdown, setIsOpenSentryDropdown] = useState(false);
  const [isOpenSentryProjectDropdown, setIsOpenSentryProjectDropdown] =
    useState(false);
  const {
    integrations,
    checkJiraStatus,
    disconnectJira,
    checkLinearStatus,
    disconnectLinear,
    checkSentryStatus,
    disconnectSentry,
    fetchSentryProjects,
    setSentryProject,
  } = useIntegrationsStore();

  const jiraDropdownRef = useRef(null);
  const linearDropdownRef = useRef(null);
  const sentryDropdownRef = useRef(null);
  const sentryProjectDropdownRef = useRef(null);

  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();
  const token = getCookie("token");

  const handleJiraClick = () => {
    if (!integrations.jira.connected) {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const url = `${API_URL}/jira/auth/${currentWorkspace?._id}`;

      const popup = window.open(
        url,
        "Jira Authorization",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
      );

      if (!popup) {
        showCustomToast("Please allow popups for this site");
        return;
      }

      // Poll for popup closure and check status
      const checkInterval = setInterval(async () => {
        if (popup.closed && currentWorkspace) {
          clearInterval(checkInterval);

          // Check if Jira was connected
          try {
            await checkJiraStatus(currentWorkspace?._id);
            setIsOpenJiraDropdown(false);

            if (integrations.jira.connected) {
              showCustomToast("Jira connected successfully", "check");
            }
          } catch (err) {
            console.error("Failed to check Jira status:", err);
          }
        }
      }, 500);
    }
  };

  const handleJiraDisconnect = async () => {
    try {
      if (currentWorkspace?._id) {
        await disconnectJira(currentWorkspace._id);

        // Also update the workspace to reflect the disconnection
        const res = await fetch(
          `${API_URL}/jira/${currentWorkspace._id}/disconnect`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        setCurrentWorkspace(data);
        setIsOpenJiraDropdown(false);
        showCustomToast("Jira disconnected successfully", "check");
      }
    } catch (err) {
      console.log(err);
      showCustomToast("Failed to disconnect Jira", "exclamation");
    }
  };

  const handleLinearClick = () => {
    if (!integrations.linear.connected) {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const url = `${API_URL}/linear/auth/${currentWorkspace?._id}`;

      const popup = window.open(
        url,
        "Linear Authorization",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
      );

      if (!popup) {
        showCustomToast("Please allow popups for this site");
        return;
      }

      // Poll for popup closure and check status
      const checkInterval = setInterval(async () => {
        if (popup.closed && currentWorkspace) {
          clearInterval(checkInterval);

          // Check if Linear was connected
          try {
            await checkLinearStatus(currentWorkspace?._id);
            setIsOpenLinearDropdown(false);

            if (integrations.linear.connected) {
              showCustomToast("Linear connected successfully", "check");
            }
          } catch (err) {
            console.error("Failed to check Linear status:", err);
          }
        }
      }, 500);
    }
  };

  const handleLinearDisconnect = async () => {
    try {
      if (currentWorkspace?._id) {
        await disconnectLinear(currentWorkspace._id);

        // Also update the workspace to reflect the disconnection
        const res = await fetch(
          `${API_URL}/linear/${currentWorkspace._id}/disconnect`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        setCurrentWorkspace(data);
        setIsOpenLinearDropdown(false);
        showCustomToast("Linear disconnected successfully", "check");
      }
    } catch (err) {
      console.log(err);
      showCustomToast("Failed to disconnect Linear", "exclamation");
    }
  };

  const handleSentryClick = async () => {
    if (!integrations.sentry.connected) {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      try {
        const res = await fetch(
          `${API_URL}/sentry/auth/${currentWorkspace?._id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await res.json();

        const popup = window.open(
          data.url,
          "Sentry Authorization",
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
        );

        if (!popup) {
          showCustomToast("Please allow popups for this site");
          return;
        }

        const checkInterval = setInterval(async () => {
          if (popup.closed && currentWorkspace) {
            clearInterval(checkInterval);

            try {
              await checkSentryStatus(currentWorkspace?._id);
              setIsOpenSentryDropdown(false);

              if (useIntegrationsStore.getState().integrations.sentry.connected) {
                showCustomToast("Sentry connected successfully", "check");
              }
            } catch (err) {
              console.error("Failed to check Sentry status:", err);
            }
          }
        }, 500);
      } catch (err) {
        console.error("Failed to get Sentry auth URL:", err);
        showCustomToast("Failed to connect Sentry", "exclamation");
      }
    }
  };

  const handleSentryProjectSelect = async (projectSlug: string) => {
    if (!currentWorkspace?._id) return;
    try {
      await setSentryProject(currentWorkspace._id, projectSlug);
      setIsOpenSentryProjectDropdown(false);
      showCustomToast("Sentry project selected", "check");
    } catch (err) {
      console.error("Failed to set Sentry project:", err);
      showCustomToast("Failed to select Sentry project", "exclamation");
    }
  };

  const handleSentryDisconnect = async () => {
    try {
      if (currentWorkspace?._id) {
        await disconnectSentry(currentWorkspace._id);
        setCurrentWorkspace({
          ...currentWorkspace,
          sentry: undefined,
        });
        setIsOpenSentryDropdown(false);
        showCustomToast("Sentry disconnected successfully", "check");
      }
    } catch (err) {
      console.log(err);
      showCustomToast("Failed to disconnect Sentry", "exclamation");
    }
  };

  useOutsideClick(jiraDropdownRef, () => {
    setIsOpenJiraDropdown(false);
  });

  useOutsideClick(linearDropdownRef, () => {
    setIsOpenLinearDropdown(false);
  });

  useOutsideClick(sentryDropdownRef, () => {
    setIsOpenSentryDropdown(false);
  });

  useOutsideClick(sentryProjectDropdownRef, () => {
    setIsOpenSentryProjectDropdown(false);
  });

  useEffect(() => {
    if (currentWorkspace) {
      checkJiraStatus(currentWorkspace?._id);
      checkLinearStatus(currentWorkspace?._id);
      checkSentryStatus(currentWorkspace?._id);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (integrations.sentry.connected && currentWorkspace?._id) {
      fetchSentryProjects(currentWorkspace._id);
    }
  }, [integrations.sentry.connected, currentWorkspace?._id]);

  const connectedCount = [
    integrations?.jira?.connected,
    integrations?.linear?.connected,
    integrations?.sentry?.connected,
  ].filter(Boolean).length;

  return (
    <div>
      <div className="font-medium text-[16px] leading-[24px] text-gray-900">
        Connections{" "}
        {connectedCount > 0 && (
          <span className="text-white/[0.24]"> · {connectedCount}</span>
        )}
        <div className="flex flex-col gap-[33px] mt-6">
          <div className="relative flex font-medium justify-between transition-all rounded-[12px] items-center h-[36px]">
            <div className="flex gap-4 items-center">
              <img src={Jira} alt="Jira" />
              Jira
            </div>

            {!integrations.jira.connected ? (
              <button
                onClick={handleJiraClick}
                className="px-3 flex rounded-[12px] text-[14px] font-medium leading-[20px] items-center bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px]"
              >
                Connect
              </button>
            ) : (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-700 rounded-[50%] mr-[10px]" />
                <p className="mr-4">
                  {integrations?.jira?.baseUrl &&
                    new URL(integrations?.jira?.baseUrl).hostname.split(".")[0]}
                </p>
                <div className="relative" ref={jiraDropdownRef}>
                  <button
                    onClick={() => {
                      setIsOpenJiraDropdown((prev) => !prev);
                    }}
                    className="w-[36px] flex items-center rounded-[12px] bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px]"
                  >
                    <img src={VerticalMenu} />
                  </button>
                  <AnimatePresence>
                    {isOpenJiraDropdown && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute w-[240px] dropdown-shadow rounded-[16px] p-1 z-[100] text-[14px] leading-[20px] bg-gray-300 top-[calc(100%+4px)] right-0"
                      >
                        <button
                          className="ring-0 px-3 gap-1.5 rounded-[12px] text-[#E93D82] transition-all hover:bg-red-alpha/[0.16] w-full h-[36px] outline-0 flex items-center justify-start"
                          onClick={handleJiraDisconnect}
                        >
                          <img src={Logout} /> Disconnect integration
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            <div className="absolute h-[1px] w-[calc(100%-28px)] right-[-20px] bg-white/[0.08] bottom-[-16px]" />
          </div>

          <div className="relative flex font-medium justify-between transition-all rounded-[12px] items-center h-[36px]">
            <div className="flex gap-4 items-center">
              <img src={Linear} alt="Linear" />
              Linear
            </div>

            {!integrations.linear.connected ? (
              <button
                onClick={handleLinearClick}
                className="px-3 flex rounded-[12px] text-[14px] font-medium leading-[20px] items-center bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px]"
              >
                Connect
              </button>
            ) : (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-700 rounded-[50%] mr-[10px]" />
                <p className="mr-4">
                  {integrations?.linear?.organizationName ||
                    integrations?.linear?.email}
                </p>
                <div className="relative" ref={linearDropdownRef}>
                  <button
                    onClick={() => {
                      setIsOpenLinearDropdown((prev) => !prev);
                    }}
                    className="w-[36px] flex items-center rounded-[12px] bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px]"
                  >
                    <img src={VerticalMenu} />
                  </button>
                  <AnimatePresence>
                    {isOpenLinearDropdown && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute w-[240px] dropdown-shadow rounded-[16px] p-1 z-[100] text-[14px] leading-[20px] bg-gray-300 top-[calc(100%+4px)] right-0"
                      >
                        <button
                          className="ring-0 px-3 gap-1.5 rounded-[12px] text-[#E93D82] transition-all hover:bg-red-alpha/[0.16] w-full h-[36px] outline-0 flex items-center justify-start"
                          onClick={handleLinearDisconnect}
                        >
                          <img src={Logout} /> Disconnect integration
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            <div className="absolute h-[1px] w-[calc(100%-28px)] right-[-20px] bg-white/[0.08] bottom-[-16px]" />
          </div>

          <div className="relative flex font-medium justify-between transition-all rounded-[12px] items-center h-[36px]">
            <div className="flex gap-2 items-center">
              <div className="flex gap-4 items-center">
                <img src={Sentry} alt="Sentry" />
                Sentry
              </div>
              <span className="px-2 py-0.5 text-[12px] leading-[16px] font-medium bg-gray-300 text-gray-900 rounded-[8px]">
                Soon
              </span>
            </div>

            {!integrations.sentry.connected ? (
              <button
                disabled
                className="px-3 flex rounded-[12px] text-[14px] font-medium leading-[20px] items-center bg-gray-300 disabled:bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px] cursor-not-allowed opacity-50"
              >
                Connect
              </button>
            ) : (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-700 rounded-[50%] mr-[10px]" />
                <div className="relative mr-4" ref={sentryProjectDropdownRef}>
                  <button
                    onClick={() =>
                      setIsOpenSentryProjectDropdown((prev) => !prev)
                    }
                    className="px-3 flex rounded-[12px] text-[14px] font-medium leading-[20px] items-center bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px] gap-1"
                  >
                    {integrations.sentry.projectSlug || "Select project"}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      className={`transition-transform ${
                        isOpenSentryProjectDropdown ? "rotate-180" : ""
                      }`}
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <AnimatePresence>
                    {isOpenSentryProjectDropdown && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute w-[240px] max-h-[200px] overflow-y-auto dropdown-shadow rounded-[16px] p-1 z-[100] text-[14px] leading-[20px] bg-gray-300 top-[calc(100%+4px)] right-0"
                      >
                        {integrations.sentry.projects?.length ? (
                          integrations.sentry.projects.map((project) => (
                            <button
                              key={project.id}
                              className="ring-0 px-3 gap-1.5 rounded-[12px] transition-all hover:bg-white/[0.08] w-full h-[36px] outline-0 flex items-center justify-between"
                              onClick={() =>
                                handleSentryProjectSelect(project.slug)
                              }
                            >
                              {project.name}
                              {integrations.sentry.projectSlug === project.slug && (
                                <img src={CheckWhite} alt="Selected" />
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-white/[0.48]">
                            No projects found
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="relative" ref={sentryDropdownRef}>
                  <button
                    onClick={() => {
                      setIsOpenSentryDropdown((prev) => !prev);
                    }}
                    className="w-[36px] flex items-center rounded-[12px] bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px]"
                  >
                    <img src={VerticalMenu} />
                  </button>
                  <AnimatePresence>
                    {isOpenSentryDropdown && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute w-[240px] dropdown-shadow rounded-[16px] p-1 z-[100] text-[14px] leading-[20px] bg-gray-300 top-[calc(100%+4px)] right-0"
                      >
                        <button
                          className="ring-0 px-3 gap-1.5 rounded-[12px] text-[#E93D82] transition-all hover:bg-red-alpha/[0.16] w-full h-[36px] outline-0 flex items-center justify-start"
                          onClick={handleSentryDisconnect}
                        >
                          <img src={Logout} /> Disconnect integration
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
            <div className="absolute h-[1px] w-[calc(100%-28px)] right-[-20px] bg-white/[0.08] bottom-[-16px]" />
          </div>

          {integrationsList.map((integration, i) => {
            return (
              <div
                key={i}
                className="relative flex font-medium justify-between transition-all rounded-[12px] items-center h-[36px]"
              >
                <div className="flex gap-2 items-center">
                  <div className="flex gap-4 items-center">
                    <img src={integration.icon} alt="Slack" />
                    {integration.name}
                  </div>
                  <span className="px-2 py-0.5 text-[12px] leading-[16px] font-medium bg-gray-300 text-gray-900 rounded-[8px]">
                    Soon
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled
                    className="px-3 flex rounded-[12px] text-[14px] font-medium leading-[20px] items-center bg-gray-300 disabled:bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px] cursor-not-allowed opacity-50"
                  >
                    Connect
                  </button>
                </div>
                {i !== integrationsList.length - 1 && (
                  <div className="absolute h-[1px] w-[calc(100%-28px)] right-[-20px] bg-white/[0.08] bottom-[-16px]" />
                )}
              </div>
            );
          })}

          {/* <div className="relative flex font-medium justify-between transition-all rounded-[12px] items-center h-[36px]">
            <div className="flex gap-2 items-center">
              <div className="flex gap-4 items-center">
                <img src={Linear} alt="Slack" />
                Linear
              </div>
              <span className="px-2 py-0.5 text-[12px] leading-[16px] font-medium bg-gray-300 text-gray-900 rounded-[8px]">
                Soon
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled
                className="px-3 flex rounded-[12px] text-[14px] font-medium leading-[20px] items-center bg-gray-300 hover:bg-gray-400 transition-all justify-center h-[36px] cursor-not-allowed opacity-50"
              >
                Connect
              </button>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default SettingsIntegrations;
